// ═══════════════════════════════════════════════════════════════
// FEMA PDF Form Field Mappings
// Maps data fields to actual AcroForm field names in FEMA PDFs
// Field names verified by filling each field with its F-index
// and visually confirming positions on the rendered PDF.
// ═══════════════════════════════════════════════════════════════

// Prefix shortcuts
const T = 'CBP303[0].FEMAFormTemplate[0].';
const F1 = 'form1[0].#subform[0].';

// ── 009-0-91  Project Worksheet ──────────────────────────────
// 85 fields (77 text + 8 checkboxes), 1 page
export const PW_FIELDS = {
  // Header row
  disasterNo:      `${T}TextField1[5]`,      // F11 — FEMA number (left part of DISASTER)
  drNumber:        `${T}TextField1[6]`,      // F12 — DR number (right part of DISASTER)
  projectNo:       `${T}TextField9[0]`,      // F14 — PROJECT #
  paId:            `${T}TextField10[0]`,     // F13 — PA ID #
  date:            `${T}DateTimeField4[0]`,  // F10 — DATE
  category:        `${T}TextField8[0]`,      // F9  — CATEGORY

  // Second row
  damagedFacility: `${T}TextField1[7]`,      // F15 — DAMAGED FACILITY
  workCompleteDate:`${T}DateTimeField5[0]`,  // F16 — WORK COMPLETE AS OF (date)
  workCompletePct: `${T}TextField1[8]`,      // F17 — WORK COMPLETE % number

  // Third row
  applicant:       `${T}TextField1[2]`,      // F5  — APPLICANT
  county:          `${T}TextField12[0]`,     // F2  — COUNTY

  // Fourth row
  location:        `${T}TextField1[3]`,      // F6  — LOCATION
  latitude:        `${T}TextField11[0]`,     // F19 — LATITUDE
  longitude:       `${T}TextField8[1]`,      // F18 — LONGITUDE

  // Large text boxes
  damageDesc:      `${T}TextField1[0]`,      // F1  — DAMAGE DESCRIPTION AND DIMENSIONS
  scopeOfWork:     `${T}TextField1[9]`,      // F20 — SCOPE OF WORK

  // Checkboxes (Yes/No pairs)
  scopeChangeYes:  `${T}CheckBox1[0]`,       // F8  — Does Scope of Work change pre-disaster? YES
  scopeChangeNo:   `${T}CheckBox1[1]`,       // F21 — ... NO
  specialConsYes:  `${T}CheckBox1[2]`,       // F22 — Special Considerations included? YES
  specialConsNo:   `${T}CheckBox1[3]`,       // F23 — ... NO
  insuranceYes:    `${T}CheckBox1[4]`,       // F24 — Insurance coverage? YES
  insuranceNo:     `${T}CheckBox1[5]`,       // F25 — ... NO
  hazMitYes:       `${T}CheckBox1[6]`,       // F26 — Hazard Mitigation included? YES
  hazMitNo:        `${T}CheckBox1[7]`,       // F27 — ... NO

  // PROJECT COST table — 9 data rows × 6 columns
  // Columns: ITEM, CODE, NARRATIVE, QUANTITY/UNIT, UNIT PRICE, COST
  costRows: [
    { item: `${T}TextField1[12]`, code: `${T}TextField1[13]`, narrative: `${T}TextField1[14]`, qty: `${T}TextField1[15]`, unitPrice: `${T}TextField1[16]`, cost: `${T}TextField1[17]` },
    { item: `${T}TextField1[23]`, code: `${T}TextField1[22]`, narrative: `${T}TextField1[21]`, qty: `${T}TextField1[20]`, unitPrice: `${T}TextField1[19]`, cost: `${T}TextField1[18]` },
    { item: `${T}TextField1[29]`, code: `${T}TextField1[28]`, narrative: `${T}TextField1[27]`, qty: `${T}TextField1[26]`, unitPrice: `${T}TextField1[25]`, cost: `${T}TextField1[24]` },
    { item: `${T}TextField1[30]`, code: `${T}TextField1[31]`, narrative: `${T}TextField1[32]`, qty: `${T}TextField1[33]`, unitPrice: `${T}TextField1[34]`, cost: `${T}TextField1[35]` },
    { item: `${T}TextField1[41]`, code: `${T}TextField1[40]`, narrative: `${T}TextField1[39]`, qty: `${T}TextField1[38]`, unitPrice: `${T}TextField1[37]`, cost: `${T}TextField1[36]` },
    { item: `${T}TextField1[47]`, code: `${T}TextField1[46]`, narrative: `${T}TextField1[45]`, qty: `${T}TextField1[44]`, unitPrice: `${T}TextField1[43]`, cost: `${T}TextField1[42]` },
    { item: `${T}TextField1[48]`, code: `${T}TextField1[49]`, narrative: `${T}TextField1[50]`, qty: `${T}TextField1[51]`, unitPrice: `${T}TextField1[52]`, cost: `${T}TextField1[53]` },
    { item: `${T}TextField1[59]`, code: `${T}TextField1[58]`, narrative: `${T}TextField1[57]`, qty: `${T}TextField1[56]`, unitPrice: `${T}TextField1[55]`, cost: `${T}TextField1[54]` },
    { item: `${T}TextField1[65]`, code: `${T}TextField1[64]`, narrative: `${T}TextField1[63]`, qty: `${T}TextField1[62]`, unitPrice: `${T}TextField1[61]`, cost: `${T}TextField1[60]` },
  ],

  // TOTAL COST (bottom right of cost table)
  totalCost:       `${T}TextField1[66]`,     // F85 — TOTAL COST

  // Footer — Prepared By / Applicant Rep
  preparedBy:      `${T}TextField1[1]`,      // F3  — PREPARED BY
  prepTitle:       `${T}TextField1[4]`,      // F7  — TITLE
  prepSig:         `${T}TextField5[0]`,      // F4  — SIGNATURE
  applicantRep:    `${T}TextField1[10]`,     // F28 — APPLICANT REP.
  applicantRepTitle:`${T}TextField1[11]`,    // F30 — TITLE
  applicantRepSig: `${T}TextField5[1]`,      // F29 — SIGNATURE
};

// ── 009-0-91D  Photo Documentation Sheet ─────────────────────
// 12 fields, 1 page
export const PHOTO_FIELDS = {
  // Header row — same layout as Project Worksheet
  disasterNo:    `${T}TextField1[3]`,      // F6  — FEMA number (left of DISASTER)
  drNumber:      `${T}TextField1[4]`,      // F7  — DR number (right of DISASTER)
  projectNo:     `${T}TextField9[0]`,      // F9  — PROJECT #
  paId:          `${T}TextField10[0]`,     // F8  — PA ID #
  date:          `${T}DateTimeField4[0]`,  // F2  — DATE
  category:      `${T}TextField8[0]`,      // F1  — CATEGORY

  // Second row
  applicant:     `${T}TextField1[2]`,      // F5  — APPLICANT
  county:        `${T}TextField1[1]`,      // F4  — COUNTY

  // Photo description areas (2 large top + 2 smaller bottom)
  photo1:        `${T}TextField1[0]`,      // F3  — top-left DESCRIPTION
  photo2:        `${T}TextField1[5]`,      // F10 — top-right DESCRIPTION
  photo3:        `${T}TextField1[6]`,      // F11 — bottom-left DESCRIPTION
  photo4:        `${T}TextField1[7]`,      // F12 — bottom-right DESCRIPTION
};

// ── 009-0-120  Special Consideration Questions ───────────────
// 42 fields (15 text + 27 checkboxes), 1 page
// Header: APPLICANT, PA ID #, DATE, PROJECT NAME, PROJECT #, LOCATION
// Then 9 questions, each with Yes/No/Unsure checkboxes + Comments text field
export const SCQ_FIELDS = {
  // Header
  applicant:    `${T}TextField1[0]`,      // F1  — APPLICANT
  paId:         `${T}TextField1[1]`,      // F2  — PA ID #
  date:         `${T}TextField1[2]`,      // F3  — DATE
  projectName:  `${T}TextField1[3]`,      // F4  — PROJECT NAME
  projectNo:    `${T}TextField1[4]`,      // F5  — PROJECT #
  location:     `${T}TextField1[5]`,      // F6  — LOCATION

  // Questions — each has 3 checkboxes (Yes/No/Unsure) + comment text field
  // Q1: Insurance/insurable risk?
  // Q2: Floodplain/coastal?
  // Q3: Coastal Barrier Resource?
  // Q4: Change pre-disaster condition?
  // Q5: Hazard mitigation proposal?
  // Q6: Historic register / older than 50 years?
  // Q7: Pristine/undisturbed areas?
  // Q8: Hazardous materials?
  // Q9: Other environmental/controversial?
  questions: [
    { // Q1 — Insurance
      yes: `${T}CheckBox1[0]`,  no: `${T}CheckBox1[1]`,  unsure: `${T}CheckBox1[2]`,
      comment: `${T}TextField1[6]`,    // F22
    },
    { // Q2 — Floodplain
      yes: `${T}CheckBox1[3]`,  no: `${T}CheckBox1[4]`,  unsure: `${T}CheckBox1[5]`,
      comment: `${T}TextField1[7]`,    // F23
    },
    { // Q3 — Coastal Barrier
      yes: `${T}CheckBox1[6]`,  no: `${T}CheckBox1[7]`,  unsure: `${T}CheckBox1[8]`,
      comment: `${T}TextField1[8]`,    // F24
    },
    { // Q4 — Change pre-disaster condition
      yes: `${T}CheckBox1[9]`,  no: `${T}CheckBox1[10]`, unsure: `${T}CheckBox1[11]`,
      comment: `${T}TextField1[9]`,    // F25
    },
    { // Q5 — Hazard mitigation
      yes: `${T}CheckBox1[12]`, no: `${T}CheckBox1[13]`, unsure: `${T}CheckBox1[14]`,
      comment: `${T}TextField1[10]`,   // F26
    },
    { // Q6 — Historic
      yes: `${T}CheckBox1[15]`, no: `${T}CheckBox1[16]`, unsure: `${T}CheckBox1[17]`,
      comment: `${T}TextField1[11]`,   // F36
    },
    { // Q7 — Pristine/undisturbed
      yes: `${T}CheckBox1[18]`, no: `${T}CheckBox1[19]`, unsure: `${T}CheckBox1[20]`,
      comment: `${T}TextField1[12]`,   // F37
    },
    { // Q8 — Hazardous materials
      yes: `${T}CheckBox1[21]`, no: `${T}CheckBox1[22]`, unsure: `${T}CheckBox1[23]`,
      comment: `${T}TextField1[14]`,   // F42 — note: Q8 comment is TextField1[14]
    },
    { // Q9 — Other environmental/controversial
      yes: `${T}CheckBox1[24]`, no: `${T}CheckBox1[25]`, unsure: `${T}CheckBox1[26]`,
      comment: `${T}TextField1[13]`,   // F41
    },
  ],
};

// ── 009-0-49  Request for Public Assistance ──────────────────
// 39 fields (35 text + 4 checkboxes), 1 page
export const RPA_FIELDS = {
  // Top section
  applicantName:    `${F1}TextField1[2]`,      // F5  — APPLICANT
  dateSubmitted:    `${F1}DateTimeField2[0]`,   // F2  — DATE SUBMITTED
  county:           `${F1}TextField1[3]`,      // F6  — COUNTY

  // Applicant Physical Location
  streetAddress:    `${F1}TextField1[0]`,      // F1  — STREET ADDRESS
  city:             `${F1}TextField1[1]`,      // F3  — CITY
  physCounty:       `${F1}TextField1[4]`,      // F7  — COUNTY (physical location)
  state:            `${F1}TextField5[0]`,      // F4  — STATE
  zip:              `${F1}TextField7[0]`,      // F8  — ZIP CODE

  // Mailing Address
  mailStreet:       `${F1}TextField1[5]`,      // F9  — STREET ADDRESS (mailing)
  mailPOBox:        `${F1}TextField1[6]`,      // F10 — POST OFFICE BOX
  mailCity:         `${F1}TextField1[7]`,      // F12 — CITY (mailing)
  mailState:        `${F1}TextField5[1]`,      // F11 — STATE (mailing)
  mailZip:          `${F1}TextField5[2]`,      // F13 — ZIP CODE (mailing)

  // Primary Contact
  primaryName:      `${F1}TextField1[8]`,      // F14 — NAME (primary)
  primaryTitle:     `${F1}TextField1[11]`,     // F17 — TITLE (primary)
  primaryPhone:     `${F1}TextField1[15]`,     // F21 — BUSINESS PHONE (primary)
  primaryFax:       `${F1}TextField1[13]`,     // F19 — FAX NUMBER (primary)
  primaryHomePhone: `${F1}TextField1[16]`,     // F22 — HOME PHONE (primary)
  primaryCell:      `${F1}TextField1[19]`,     // F25 — CELL PHONE (primary)
  primaryEmail:     `${F1}TextField1[20]`,     // F26 — E-MAIL ADDRESS (primary)
  primaryPager:     `${F1}TextField1[22]`,     // F28 — PAGER & PIN (primary)

  // Alternate Contact
  altName:          `${F1}TextField1[9]`,      // F15 — NAME (alternate)
  altTitle:         `${F1}TextField1[10]`,     // F16 — TITLE (alternate)
  altPhone:         `${F1}TextField1[14]`,     // F20 — BUSINESS PHONE (alternate)
  altFax:           `${F1}TextField1[12]`,     // F18 — FAX NUMBER (alternate)
  altHomePhone:     `${F1}TextField1[17]`,     // F23 — HOME PHONE (alternate)
  altCell:          `${F1}TextField1[18]`,     // F24 — CELL PHONE (alternate)
  altEmail:         `${F1}TextField1[23]`,     // F29 — E-MAIL ADDRESS (alternate)
  altPager:         `${F1}TextField1[21]`,     // F27 — PAGER & PIN (alternate)

  // PDA / PNP checkboxes
  pdaYes:           `${F1}CheckBox1[0]`,       // F30 — PDA? YES
  pdaNo:            `${F1}CheckBox1[1]`,       // F31 — PDA? NO
  pnpYes:           `${F1}CheckBox1[2]`,       // F32 — Private Non-Profit? YES
  pnpNo:            `${F1}CheckBox1[3]`,       // F33 — Private Non-Profit? NO

  // PNP type description
  pnpFacilityType:  `${F1}TextField1[24]`,     // F34 — facility type description

  // Official Use Only footer
  officialFema:     `${F1}TextField1[25]`,     // F35 — FEMA number
  officialDr:       `${F1}TextField1[26]`,     // F36 — DR number
  officialFips:     `${F1}TextField1[27]`,     // F37 — FIPS#
  officialOther:    `${F1}TextField1[28]`,     // F38 — (additional)
  officialDate:     `${F1}DateTimeField3[0]`,  // F39 — DATE RECEIVED
};

// ── 009-0-126  Contract Work Summary Record ──────────────────
// 55 fields, 1 page — landscape layout
export const CONTRACT_FIELDS = {
  // Page header
  pageNo:       `${T}TextField8[0]`,      // F1  — PAGE
  ofPages:      `${T}TextField8[1]`,      // F2  — OF

  // Top section — 4 columns
  date:         `${T}Namebox[0]`,         // F3  — DATE
  paId:         `${T}Namebox[1]`,         // F4  — PA ID #
  projectNo:    `${T}Namebox[2]`,         // F5  — PROJECT #
  disasterNo:   `${T}Namebox[3]`,         // F6  — DISASTER

  // Second header row
  location:     `${T}Namebox[4]`,         // F7  — LOCATION/SITE
  category:     `${T}Namebox[5]`,         // F8  — CATEGORY
  periodCovering:`${T}Namebox[6]`,        // F9  — PERIOD COVERING

  // Description of work performed
  description:  `${T}Namebox[7]`,         // F10 — DESCRIPTION OF WORK PERFORMED

  // Table: 8 rows × 5 columns
  // Columns: DATES WORKED, CONTRACTOR, BILLING/INVOICE NUMBER, AMOUNT, COMMENTS-SCOPE
  rows: [
    { dates: `${T}TextField9[0]`,  contractor: `${T}TextField9[9]`,  invoice: `${T}TextField9[16]`, amount: `${T}TextField9[24]`, comments: `${T}TextField9[32]` },
    { dates: `${T}TextField9[1]`,  contractor: `${T}TextField9[8]`,  invoice: `${T}TextField9[17]`, amount: `${T}TextField9[25]`, comments: `${T}TextField9[33]` },
    { dates: `${T}TextField9[2]`,  contractor: `${T}TextField9[10]`, invoice: `${T}TextField9[18]`, amount: `${T}TextField9[26]`, comments: `${T}TextField9[34]` },
    { dates: `${T}TextField9[3]`,  contractor: `${T}TextField9[11]`, invoice: `${T}TextField9[19]`, amount: `${T}TextField9[27]`, comments: `${T}TextField9[35]` },
    { dates: `${T}TextField9[4]`,  contractor: `${T}TextField9[12]`, invoice: `${T}TextField9[20]`, amount: `${T}TextField9[28]`, comments: `${T}TextField9[36]` },
    { dates: `${T}TextField9[5]`,  contractor: `${T}TextField9[13]`, invoice: `${T}TextField9[21]`, amount: `${T}TextField9[29]`, comments: `${T}TextField9[37]` },
    { dates: `${T}TextField9[6]`,  contractor: `${T}TextField9[14]`, invoice: `${T}TextField9[22]`, amount: `${T}TextField9[30]`, comments: `${T}TextField9[38]` },
    { dates: `${T}TextField9[7]`,  contractor: `${T}TextField9[15]`, invoice: `${T}TextField9[23]`, amount: `${T}TextField9[31]`, comments: `${T}TextField9[39]` },
  ],

  // Totals
  grandTotalLabel: `${T}TextField2[1]`,    // F55 — GRAND TOTAL left cell
  grandTotal:      `${T}TextField2[0]`,    // F51 — GRAND TOTAL amount

  // Footer
  certifiedBy:  `${T}Namebox[8]`,         // F52 — CERTIFIED
  certTitle:    `${T}Namebox[9]`,         // F53 — TITLE
  certDate:     `${T}Namebox[10]`,        // F54 — DATE
};

// ── 009-0-123  Force Account Labor Summary ───────────────────
// 126 fields, 1 page — landscape layout
export const LABOR_FIELDS = {
  // Top section
  applicant:    `${T}Namebox[6]`,         // F7  — APPLICANT
  paId:         `${T}Namebox[5]`,         // F6  — PA ID #
  projectNo:    `${T}Namebox[4]`,         // F5  — PROJECT #
  disasterNo:   `${T}Namebox[3]`,         // F4  — DISASTER
  location:     `${T}Namebox[2]`,         // F3  — LOCATION/SITE
  category:     `${T}Namebox[1]`,         // F2  — CATEGORY
  periodCovering:`${T}Namebox[0]`,        // F1  — PERIOD COVERING

  // Page header
  pageNo:       `${T}TextField8[0]`,      // F8  — PAGE
  ofPages:      `${T}TextField8[1]`,      // F9  — OF

  // Description of work performed
  description:  `${T}Namebox[7]`,         // F121 — DESCRIPTION OF WORK PERFORMED

  // Date headers (7 day columns)
  day1: `${T}TextField2[0]`, day2: `${T}TextField3[0]`, day3: `${T}TextField4[0]`,
  day4: `${T}TextField5[0]`, day5: `${T}TextField6[0]`, day6: `${T}TextField7[0]`,
  day7: `${T}TextField8[2]`,

  // 4 employees × 2 rows each (REG + OT)
  // Each employee: name, jobTitle, 7 day hours, then 5 cost cols (totalHrs, hourlyRate, benefitRate, totalHourlyRate, totalCosts)
  employees: [
    { // Employee 1
      name: `${T}TextField1[0]`, jobTitle: `${T}TextField1[1]`,
      // REG row
      reg: { d1: `${T}TextField2[1]`, d2: `${T}TextField3[1]`, d3: `${T}TextField4[1]`, d4: `${T}TextField5[1]`, d5: `${T}TextField6[1]`, d6: `${T}TextField7[1]`, d7: `${T}TextField8[3]`,
             totalHrs: `${T}TextField8[9]`, hourlyRate: `${T}TextField8[10]`, benefitRate: `${T}TextField8[11]`, totalHourlyRate: `${T}TextField8[12]`, totalCosts: `${T}TextField8[13]` },
      // OT row
      ot:  { d1: `${T}TextField2[2]`, d2: `${T}TextField3[2]`, d3: `${T}TextField4[2]`, d4: `${T}TextField5[2]`, d5: `${T}TextField6[2]`, d6: `${T}TextField7[2]`, d7: `${T}TextField8[4]`,
             totalHrs: `${T}TextField8[18]`, hourlyRate: `${T}TextField8[17]`, benefitRate: `${T}TextField8[14]`, totalHourlyRate: `${T}TextField8[16]`, totalCosts: `${T}TextField8[15]` },
    },
    { // Employee 2
      name: `${T}TextField1[2]`, jobTitle: `${T}TextField1[3]`,
      reg: { d1: `${T}TextField2[3]`, d2: `${T}TextField3[3]`, d3: `${T}TextField4[3]`, d4: `${T}TextField5[3]`, d5: `${T}TextField6[3]`, d6: `${T}TextField7[3]`, d7: `${T}TextField8[5]`,
             totalHrs: `${T}TextField8[23]`, hourlyRate: `${T}TextField8[22]`, benefitRate: `${T}TextField8[19]`, totalHourlyRate: `${T}TextField8[21]`, totalCosts: `${T}TextField8[20]` },
      ot:  { d1: `${T}TextField2[4]`, d2: `${T}TextField3[4]`, d3: `${T}TextField4[4]`, d4: `${T}TextField5[4]`, d5: `${T}TextField6[4]`, d6: `${T}TextField7[4]`, d7: `${T}TextField8[6]`,
             totalHrs: `${T}TextField8[28]`, hourlyRate: `${T}TextField8[27]`, benefitRate: `${T}TextField8[24]`, totalHourlyRate: `${T}TextField8[26]`, totalCosts: `${T}TextField8[25]` },
    },
    { // Employee 3
      name: `${T}TextField1[4]`, jobTitle: `${T}TextField1[5]`,
      reg: { d1: `${T}TextField2[5]`, d2: `${T}TextField3[5]`, d3: `${T}TextField4[5]`, d4: `${T}TextField5[5]`, d5: `${T}TextField6[5]`, d6: `${T}TextField7[5]`, d7: `${T}TextField8[7]`,
             totalHrs: `${T}TextField8[33]`, hourlyRate: `${T}TextField8[32]`, benefitRate: `${T}TextField8[29]`, totalHourlyRate: `${T}TextField8[31]`, totalCosts: `${T}TextField8[30]` },
      ot:  { d1: `${T}TextField2[6]`, d2: `${T}TextField3[6]`, d3: `${T}TextField4[6]`, d4: `${T}TextField5[6]`, d5: `${T}TextField6[6]`, d6: `${T}TextField7[6]`, d7: `${T}TextField8[8]`,
             totalHrs: `${T}TextField8[38]`, hourlyRate: `${T}TextField8[37]`, benefitRate: `${T}TextField8[34]`, totalHourlyRate: `${T}TextField8[36]`, totalCosts: `${T}TextField8[35]` },
    },
    { // Employee 4 (last row — uses TextField1[7]/[6] for name/title)
      name: `${T}TextField1[7]`, jobTitle: `${T}TextField1[6]`,
      reg: { d1: `${T}TextField2[7]`, d2: `${T}TextField2[9]`, d3: `${T}TextField2[11]`, d4: `${T}TextField2[13]`, d5: `${T}TextField2[15]`, d6: `${T}TextField2[17]`, d7: `${T}TextField2[19]`,
             totalHrs: `${T}TextField8[39]`, hourlyRate: `${T}TextField8[41]`, benefitRate: `${T}TextField8[43]`, totalHourlyRate: `${T}TextField8[45]`, totalCosts: `${T}TextField8[47]` },
      ot:  { d1: `${T}TextField2[8]`, d2: `${T}TextField2[10]`, d3: `${T}TextField2[12]`, d4: `${T}TextField2[14]`, d5: `${T}TextField2[16]`, d6: `${T}TextField2[18]`, d7: `${T}TextField2[20]`,
             totalHrs: `${T}TextField8[40]`, hourlyRate: `${T}TextField8[42]`, benefitRate: `${T}TextField8[44]`, totalHourlyRate: `${T}TextField8[46]`, totalCosts: `${T}TextField8[48]` },
    },
  ],

  // Totals
  totalRegular: `${T}TextField8[49]`,     // F122 — TOTAL COSTS FOR FORCE ACCOUNT LABOR REGULAR TIME
  totalOvertime:`${T}TextField8[50]`,     // F123 — TOTAL COST FOR FORCE ACCOUNT LABOR OVERTIME

  // Footer
  certifiedBy:  `${T}Namebox[8]`,         // F124 — CERTIFIED
  certTitle:    `${T}Namebox[9]`,         // F125 — TITLE
  certDate:     `${T}Namebox[10]`,        // F126 — DATE
};
